// Click-to-open popover for the "Grid queue (15 km)" cell in Projects
// compare. Surfaces the same per-project list AnalysisPanel shows under
// "Check grid feasibility & queue". Mirrors the ProvenancePopover pattern:
// portal-rendered to document.body so the table's overflow:auto can't clip.
import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';

const dropColor = (band) =>
  band === 'high'   ? '#4ade80'
: band === 'medium' ? '#fbbf24'
:                     '#f87171';

export function GridQueueCell({ queue, siteName }) {
  const btnRef = useRef(null);
  const [anchor, setAnchor] = useState(null);

  useEffect(() => {
    if (!anchor) return;
    const close = () => setAnchor(null);
    const onClick = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('.queue-pop')) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [anchor]);

  if (!queue) return '—';
  const projects = queue.projects || [];
  const totalMw = Math.round(queue.total_queue_mw || 0);
  const label = `${totalMw.toLocaleString()} MW · ${projects.length} project${projects.length === 1 ? '' : 's'}`;

  // No projects → just plain text, no clickable popover.
  if (projects.length === 0) return label;

  const onToggle = (e) => {
    e.stopPropagation();
    if (anchor) { setAnchor(null); return; }
    setAnchor(e.currentTarget.getBoundingClientRect());
  };

  return (
    <>
      <button ref={btnRef} type="button" className="queue-cell-btn"
              onClick={onToggle} aria-label="Show queue projects">
        {label} <span className="queue-cell-chevron">▾</span>
      </button>
      {anchor && (
        <GridQueuePopover queue={queue} siteName={siteName} anchor={anchor} />
      )}
    </>
  );
}

function GridQueuePopover({ queue, siteName, anchor }) {
  const POP_W = 420;
  const margin = 8;
  const left = Math.min(
    Math.max(margin, anchor.left + anchor.width / 2 - POP_W / 2),
    window.innerWidth - POP_W - margin,
  );
  const top = anchor.bottom + 6;

  const totalMw = Math.round(queue.total_queue_mw || 0);
  const projects = queue.projects || [];

  return createPortal(
    <div className="queue-pop" style={{ top, left, width: POP_W }}
         role="dialog" aria-label="Grid queue projects">
      <div className="queue-pop-header">
        <span className="queue-pop-title">
          Queue projects within 15 km
          {siteName ? ` · ${siteName}` : ''}
        </span>
        <span className="queue-pop-total">{totalMw.toLocaleString()} MW total</span>
      </div>
      <table className="queue-pop-table">
        <thead>
          <tr>
            <th>Project</th>
            <th className="num">MW</th>
            <th className="num">Drop prob.</th>
            <th className="num">Target</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p, i) => (
            <tr key={i}>
              <td>
                <div className="queue-pop-name">{p.project_name || '—'}</div>
                {p.project_type && (
                  <div className="queue-pop-type">{p.project_type}</div>
                )}
              </td>
              <td className="num">{p.requested_mw ?? '—'}</td>
              <td className="num" style={{ color: dropColor(p.drop_probability_band) }}>
                {p.drop_probability != null ? `${Math.round(p.drop_probability * 100)}%` : '—'}
              </td>
              <td className="num queue-pop-target">
                {p.target_connection_date?.slice(0, 7) || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="queue-pop-footer">
        Source: Fingrid grid-feasibility queue API · drop probability
        is the model's estimate that the project will leave the queue
        before connection (high drop = more capacity becomes available).
      </div>
    </div>,
    document.body,
  );
}
